/**
 * Fixture data for `?fixture=main` — a RAW `GET /api/cockpit/today` envelope
 * in the exact shape `azure-functions/cis/shared/cockpit.js` (`todayView`)
 * and `CisCockpitToday/index.js` produce. Only the transport (MSAL + fetch)
 * is swapped in fixture mode: this envelope runs through the same
 * `shapeTodayScreen` / `toTodayViewModel` pipeline (src/features/sm/api/
 * shape.js) the live app uses, so a field-name mismatch between the shaper
 * and the screen can't hide behind a pre-shaped fixture again.
 *
 * Visible text (titles, dates, ids) is copied verbatim from
 * mockups/cockpit-v3/Main.dc.html; those are the artboard's own
 * illustrative samples, not records (mockups/cockpit-v3/README.md "Sample
 * data note"). `dutyDept`/`assignedTo` carry the artboard's fictional
 * sample names — allowed by the same note.
 */

"use strict";

export const fixtureMainEnvelope = {
  generatedAtUtc: "2026-09-02T09:00:00.000Z",
  counts: { today: 8, ncrs: 3, reports: 2 },
  errors: [],
  projects: [
    {
      code: "SS412413",
      groups: {
        reinspectionsOverdue: [
          {
            inspectionNumber: 1842,
            clientCaptureId: "CI-2026-1842",
            title: "Bracket weld, frame 88",
            comments: "",
            category: "Structural",
            stage: "Rectification",
            inspectionDate: "2026-08-24",
            ageDays: 11,
            assignedTo: "j.mendoza@example.com",
          },
        ],
        ncrsRecorded: [
          {
            ncrNumber: 501,
            title: "Undersize weld leg, port bracket fr.88",
            raisedDate: "2026-08-27",
            ageDays: 6,
            dutyDept: "j.mendoza@example.com",
            status: "Recorded",
            photoCount: 3,
          },
        ],
        resultsMissing: [
          {
            scheduleRef: "CI-2026-1821",
            title: "Ballast tank coating DFT",
            plannedDate: "2026-08-30",
            daysLate: 3,
            assignedTo: "j.mendoza@example.com",
            scheduleStatus: "Planned",
            linkedInspectionNumber: 0,
            sourceInspectionNumber: 1821,
          },
        ],
      },
      onSiteToday: [
        {
          scheduleRef: "ITP-PA-052",
          title: "Ballast tank final, No.5 WBT",
          category: "Paint QA",
          assignedTo: "j.mendoza@example.com",
          plannedDate: "2026-09-02",
          plannedTime: "09:00",
        },
      ],
      reportReadiness: {
        weekStart: "2026-08-24",
        lines: [
          { id: "inspection_photos", count: 2, green: false },
          { id: "inspection_results", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        allGreen: false,
      },
    },
    {
      code: "SS418",
      groups: {
        reinspectionsOverdue: [
          {
            inspectionNumber: 956,
            clientCaptureId: "CI-2026-0956",
            title: "FO piping flange seep",
            comments: "",
            category: "Piping",
            stage: "Rectification",
            inspectionDate: "2026-08-26",
            ageDays: 9,
            assignedTo: "t.aoki@example.com",
          },
        ],
        ncrsRecorded: [
          {
            ncrNumber: 502,
            title: "Duct hanger missing, accomm. HVAC",
            raisedDate: "2026-08-29",
            ageDays: 4,
            dutyDept: "t.aoki@example.com",
            status: "Recorded",
            photoCount: 2,
          },
        ],
        resultsMissing: [
          {
            scheduleRef: "ITP-MO-071",
            title: "Main engine chock",
            plannedDate: "2026-08-31",
            daysLate: 2,
            assignedTo: "t.aoki@example.com",
            scheduleStatus: "Planned",
            linkedInspectionNumber: 0,
            sourceInspectionNumber: 0,
          },
        ],
      },
      onSiteToday: [
        {
          scheduleRef: "ITP-EO-033",
          title: "Cable transit sealing, ER flat",
          category: "Electric shop",
          assignedTo: "t.aoki@example.com",
          plannedDate: "2026-09-02",
          plannedTime: "13:30",
        },
      ],
      reportReadiness: {
        weekStart: "2026-08-24",
        lines: [
          { id: "inspection_photos", count: 0, green: true },
          { id: "inspection_results", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        allGreen: true,
      },
    },
    {
      code: "SS411",
      groups: {
        reinspectionsOverdue: [
          {
            inspectionNumber: 3011,
            clientCaptureId: "CI-2026-3011",
            title: "Cable tray supports, deck 3",
            comments: "",
            category: "Structural",
            stage: "Rectification",
            inspectionDate: "2026-08-27",
            ageDays: 8,
            assignedTo: "r.fields@example.com",
          },
        ],
        ncrsRecorded: [
          {
            ncrNumber: 503,
            title: "Coating holiday, A-deck aft",
            raisedDate: "2026-08-31",
            ageDays: 2,
            dutyDept: "r.fields@example.com",
            status: "Recorded",
            photoCount: 1,
          },
        ],
        resultsMissing: [],
      },
      onSiteToday: [],
      reportReadiness: {
        weekStart: "2026-08-24",
        lines: [
          { id: "inspection_photos", count: 0, green: true },
          { id: "inspection_results", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        allGreen: true,
      },
    },
  ],
};

/**
 * Fixture for `GET /api/cockpit/reports` — RAW shape per
 * `azure-functions/cis/CisCockpitReports/index.js`: per project, the same
 * readiness computation as Today plus the report archive (`reportRow` in
 * `shared/report-state.js`). Drives the "Week 35 reports" side card exactly
 * as the artboard shows it: SS412413 has an open readiness violation (its
 * chip wins regardless of archive state), SS418 is all-green with no report
 * yet for the week ("ready to confirm"), SS411 is all-green with a Draft
 * report already on file ("draft v1").
 */
export const fixtureReportsEnvelope = {
  generatedAtUtc: "2026-09-02T09:00:00.000Z",
  weekStart: "2026-08-24",
  errors: [],
  projects: [
    {
      code: "SS412413",
      readiness: {
        weekStart: "2026-08-24",
        lines: [
          { id: "inspection_photos", count: 2, green: false },
          { id: "inspection_results", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        allGreen: false,
      },
      reports: [],
    },
    {
      code: "SS418",
      readiness: {
        weekStart: "2026-08-24",
        lines: [
          { id: "inspection_photos", count: 0, green: true },
          { id: "inspection_results", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        allGreen: true,
      },
      reports: [],
    },
    {
      code: "SS411",
      readiness: {
        weekStart: "2026-08-24",
        lines: [
          { id: "inspection_photos", count: 0, green: true },
          { id: "inspection_results", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        allGreen: true,
      },
      reports: [
        { reportNumber: 7, weekStart: "2026-08-24", reportStatus: "Draft", generationCount: 1 },
      ],
    },
  ],
};

/** Presentational-only fixture metadata — not part of the cockpit data contract. */
export const fixtureMainMeta = {
  identity: { initials: "SL", role: "Site manager" },
  weekLabel: "W36 · Wed 2 Sep",
};
